import {
  type CpdModuleSummary,
  type CpdModuleProgress,
  useListModules,
  useGetMyModuleProgress,
} from "@workspace/api-client-react";
import { useLocation } from "wouter";
import {
  BookOpenCheck,
  Clock,
  Award,
  CheckCircle2,
  Circle,
  Star,
  ChevronRight,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

const difficultyConfig = {
  foundation: { label: "Foundation", class: "bg-green-50 text-green-700 border-green-200" },
  intermediate: { label: "Intermediate", class: "bg-amber-50 text-amber-700 border-amber-200" },
  advanced: { label: "Advanced", class: "bg-purple-50 text-purple-700 border-purple-200" },
};

const badgeColorConfig: Record<string, string> = {
  teal: "from-teal-600 to-teal-700",
  indigo: "from-indigo-600 to-indigo-700",
  purple: "from-purple-600 to-purple-700",
  amber: "from-amber-500 to-amber-600",
  yellow: "from-yellow-500 to-yellow-600",
};

export function CpdModulesContent({ moduleBasePath, role }: { moduleBasePath: string; role?: string }) {
  const [, setLocation] = useLocation();
  const { data: modules = [] } = useListModules(undefined);
  const { data: progressList = [] } = useGetMyModuleProgress();

  const typedModules = modules as CpdModuleSummary[];
  const typedProgress = progressList as CpdModuleProgress[];

  const progressByModuleId = Object.fromEntries(
    typedProgress.map((p) => [p.moduleId, p])
  );

  const completedModules = typedModules.filter((m) => progressByModuleId[m.id]?.completedAt != null);
  const inProgressModules = typedModules.filter((m) => {
    const p = progressByModuleId[m.id];
    return p && !p.completedAt && (p.sectionsCompleted?.length ?? 0) > 0;
  });

  const totalCpdPoints = completedModules.reduce((sum, m) => sum + m.cpdPoints, 0);

  return (
    <div>
      {(completedModules.length > 0 || inProgressModules.length > 0) && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-teal-700">{completedModules.length}</p>
            <p className="text-xs text-teal-600 mt-0.5">Modules completed</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">{inProgressModules.length}</p>
            <p className="text-xs text-amber-600 mt-0.5">In progress</p>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-indigo-700">{totalCpdPoints}</p>
            <p className="text-xs text-indigo-600 mt-0.5">CPD points earned</p>
          </div>
        </div>
      )}

      {typedModules.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-white rounded-2xl border border-slate-200">
          <BookOpenCheck className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="font-medium">No modules available yet</p>
          <p className="text-sm mt-1">CPD modules will appear here once they are published.</p>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {typedModules.map((mod) => {
            const p = progressByModuleId[mod.id];
            const completedCount = p?.sectionsCompleted?.length ?? 0;
            const totalSections = mod.sectionCount;
            const pct = totalSections > 0 ? Math.round((completedCount / totalSections) * 100) : 0;
            const isCompleted = !!p?.completedAt;
            const isStarted = completedCount > 0;
            const diffCfg = difficultyConfig[mod.difficulty];
            const bgGradient = badgeColorConfig[mod.badgeColor] ?? "from-teal-600 to-teal-700";

            return (
              <div
                key={mod.id}
                role="button"
                tabIndex={0}
                onClick={() => setLocation(`${moduleBasePath}/modules/${mod.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setLocation(`${moduleBasePath}/modules/${mod.id}`); }}
                className="group bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-300 transition-all cursor-pointer flex flex-col overflow-hidden"
              >
                <div className={`h-1.5 bg-gradient-to-r ${bgGradient}`} />
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex flex-wrap gap-2 mb-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${diffCfg.class}`}>
                      {diffCfg.label}
                    </span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full border border-slate-200">
                      {mod.topicArea}
                    </span>
                  </div>

                  <h3 className="font-bold text-slate-900 text-base leading-snug mb-2 group-hover:text-teal-700 transition-colors">
                    {mod.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
                    {mod.description}
                  </p>

                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {mod.estimatedMinutes} min
                    </span>
                    <span className="flex items-center gap-1">
                      <Award className="h-3.5 w-3.5" />
                      {mod.cpdPoints} CPD point{mod.cpdPoints !== 1 ? "s" : ""}
                    </span>
                    <span className="text-slate-400">{totalSections} sections</span>
                  </div>

                  {isCompleted ? (
                    <div className="flex items-center gap-2 p-3 bg-teal-50 rounded-xl border border-teal-200">
                      <CheckCircle2 className="h-5 w-5 text-teal-600 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-teal-700">Completed</p>
                        <p className="text-xs text-teal-600">{mod.badgeName} badge earned</p>
                      </div>
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-400 ml-auto" />
                    </div>
                  ) : isStarted ? (
                    <div>
                      <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                        <span>{completedCount}/{totalSections} sections</span>
                        <span>{pct}%</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                      <p className="text-xs text-amber-600 font-medium mt-1.5 flex items-center gap-1">
                        <Circle className="h-3 w-3" />
                        In progress — continue
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-sm text-teal-600 font-semibold pt-3 border-t border-slate-100">
                      <span>Start module</span>
                      <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {completedModules.length > 0 && (
        <div className="mt-8">
          <h3 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Award className="h-5 w-5 text-teal-600" />
            Your Badges
          </h3>
          <div className="flex flex-wrap gap-3">
            {completedModules.map((mod) => {
              const bgGradient = badgeColorConfig[mod.badgeColor] ?? "from-teal-600 to-teal-700";
              return (
                <div
                  key={mod.id}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-full text-white text-sm font-semibold bg-gradient-to-r ${bgGradient} shadow-sm`}
                >
                  <Award className="h-4 w-4" />
                  {mod.badgeName}
                  <Badge variant="outline" className="text-white border-white/40 text-xs ml-1">
                    {mod.cpdPoints}pt
                  </Badge>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-8 bg-teal-900 rounded-2xl p-8 text-white">
        <h3 className="text-lg font-bold mb-2">About CPD Modules</h3>
        <p className="text-teal-200 text-sm leading-relaxed max-w-xl">
          Each module is mapped to professional standards and clinical guidelines. Completing modules earns CPD points and digital badges that can be recorded in your portfolio for revalidation. Modules include reading, knowledge quizzes, and guided reflection activities.
        </p>
      </div>
    </div>
  );
}
